/**
 * Screen 6 — seat selection, and the hold countdown.
 *
 * The timer here is a RENDERING of `held_until`, never the thing that
 * releases the seat (non-negotiable 6). It is deliberately recomputed from
 * the server's timestamp on every tick rather than decremented, so a phone
 * that slept through five minutes shows 00:00 on wake instead of the value
 * it was at when the tab was backgrounded.
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { LocalisedField } from "../components/Localised";
import { ErrorNote, Note, Shell } from "../components/Shell";
import type { Locale } from "../i18n";
import {
  api,
  errorCode,
  isSignedIn,
  OfflineError,
  type BookingOut,
  type ProgramDetail,
  type SeatMap,
} from "../lib/api";
import { countdown, formatIdr } from "../lib/format";
import { loadTickets } from "../lib/tickets";

export default function Seats({ locale }: { locale: Locale }) {
  const { slug = "" } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [program, setProgram] = useState<ProgramDetail | null>(null);
  const [map, setMap] = useState<SeatMap | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [booking, setBooking] = useState<BookingOut | null>(null);
  const [remaining, setRemaining] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Seats this member already holds for this programme. "That one is taken"
  // and "that one is yours" are not the same message.
  //
  // Seeded from the tickets saved on the device so it is right offline and
  // on the first paint, then corrected from the server -- the device only
  // knows about tickets whose screen has actually been opened, which is not
  // the same as the tickets the member holds.
  const [mine, setMine] = useState<Set<string>>(
    () => new Set(loadTickets().filter((x) => x.program.slug === slug).flatMap((x) => x.seats)),
  );

  const loadMap = useCallback(() => {
    api
      .seats(slug)
      .then(setMap)
      .catch((err) =>
        setError(err instanceof OfflineError ? t("error.network") : t("error.generic")),
      );
  }, [slug, t]);

  useEffect(() => {
    api.program(slug, locale).then(setProgram).catch(() => undefined);
    loadMap();
    if (isSignedIn()) {
      api
        .myBookings(locale)
        .then((b) =>
          setMine(
            new Set(
              b.upcoming
                .filter(
                  (x) =>
                    x.program.slug === slug &&
                    (x.state === "PAID" || x.state === "CHECKED_IN"),
                )
                .flatMap((x) => x.seats.map((seat) => seat.label)),
            ),
          ),
        )
        .catch(() => undefined);
    }
  }, [slug, locale, loadMap]);

  // Recomputed from the server's held_until, not decremented locally.
  useEffect(() => {
    if (!booking?.held_until) return;
    const deadline = new Date(booking.held_until).getTime();
    const tick = () => {
      const left = deadline - Date.now();
      setRemaining(left);
      if (left <= 0) {
        // The server released these some seconds ago. Say so and reload the
        // map rather than letting the member carry on toward a checkout that
        // will refuse them.
        setBooking(null);
        setSelected([]);
        setError(t("error.hold_expired"));
        loadMap();
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [booking, loadMap, t]);

  const toggle = (id: number, state: string) => {
    if (state !== "AVAILABLE" || booking) return;
    setSelected((current) =>
      current.includes(id) ? current.filter((s) => s !== id) : [...current, id],
    );
  };

  const priceOf = (tierId: number | null) =>
    program?.tiers.find((tier) => tier.id === tierId)?.price_idr ?? 0;

  const subtotal = (map?.rows ?? [])
    .flatMap((row) => row.seats)
    .filter((seat) => selected.includes(seat.id))
    .reduce((sum, seat) => sum + priceOf(seat.tier_id), 0);

  async function hold() {
    setBusy(true);
    setError(null);
    try {
      const held = await api.hold(slug, { seat_ids: selected });
      setBooking(held);
      loadMap();
    } catch (err) {
      const code = errorCode(err);
      // The server's code is translated here; its English message never
      // reaches the screen.
      setError(code ? t(`error.${code}`, { defaultValue: t("error.generic") }) : t("error.generic"));
      loadMap();
      setSelected([]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell area="programs" title={t("seats.title")}>
      <LocalisedField content={program?.text} field="name" as="h2" className="screen__subtitle" />

      <div className="hall">
        <p className="stage">{t("seats.stage")}</p>

        {map?.rows.map((row) => (
          <div key={row.row} className="seat-row">
            {/* The row letter sits in a margin column on BOTH sides, the way a
                printed seat plan sets it — a member reading "A3" off their
                ticket finds the row by scanning either edge. */}
            <span className="seat-row__label" aria-hidden="true">{row.row}</span>
            <div className="seat-row__seats">
              {row.seats.map((seat) => {
                const isSelected = selected.includes(seat.id);
                return (
                  <button
                    key={seat.id}
                    type="button"
                    className="seat"
                    data-seat={
                      isSelected ? "SELECTED" : mine.has(seat.label) ? "MINE" : seat.state
                    }
                    disabled={seat.state !== "AVAILABLE" || Boolean(booking)}
                    aria-pressed={isSelected}
                    aria-label={`${seat.label} — ${t(`seat_state.${seat.state}`)}`}
                    onClick={() => toggle(seat.id, seat.state)}
                  >
                    {seat.number}
                  </button>
                );
              })}
            </div>
            <span className="seat-row__label" aria-hidden="true">{row.row}</span>
          </div>
        ))}
      </div>

      {map && (
        <p className="fallback-note seats-left">
          {t("programs.seats_left", {
            count: map.rows.flatMap((r) => r.seats).filter((x) => x.state === "AVAILABLE").length,
            total: map.rows.flatMap((r) => r.seats).length,
          })}
        </p>
      )}

      <ul className="legend">
        {(["AVAILABLE", "SOLD"] as const).map((state) => (
          <li key={state}>
            <span className="seat seat--swatch" data-seat={state} aria-hidden="true" />
            {t(`seat_state.${state}`)}
          </li>
        ))}
        <li>
          <span className="seat seat--swatch" data-seat="SELECTED" aria-hidden="true" />
          {t("seats.selected_label")}
        </li>
        {/* Only worth explaining to someone who actually has a seat here. */}
        {mine.size > 0 && (
          <li>
            <span className="seat seat--swatch" data-seat="MINE" aria-hidden="true" />
            {t("seat_state.MINE")}
          </li>
        )}
      </ul>

      {error && <ErrorNote>{error}</ErrorNote>}

      {booking ? (
        <div className="summary">
          {/* The countdown the member sees. The seats are already theirs on
              the server, and the server is what gives them back. */}
          <p aria-live="polite">
            {/* Steady until the final minute. Motion that means something,
                rather than motion for its own sake. */}
            <span className="countdown" data-urgent={remaining < 60_000}>
              {t("seats.hold_expires", { time: countdown(remaining) })}
            </span>
          </p>
          <p>
            {booking.seats.map((s) => s.label).join(", ")}
          </p>
          <p className="summary__total money">{formatIdr(booking.total_idr)}</p>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => navigate(`/checkout/${booking.ref}`)}
          >
            {t("common.continue")}
          </button>
        </div>
      ) : (
        /* Until a seat is chosen there is nothing to confirm, so the bar is a
           line of guidance rather than a slab with a dead button in it. */
        selected.length === 0 ? (
          <p className="fallback-note">{t("seats.pick_a_seat")}</p>
        ) : (
          <div className="summary">
            <p className="summary__total money">{formatIdr(subtotal)}</p>
            <button type="button" className="btn btn--primary" disabled={busy} onClick={hold}>
              {busy ? t("common.loading") : t("common.continue")}
            </button>
          </div>
        )
      )}

      {!map && !error && <Note>{t("common.loading")}</Note>}
    </Shell>
  );
}
